import {
  Column,
  // IconCounter,
  // MailIcon,
  TablePaginated,
  TablePaginatedOptions,
} from '@acceso/design-system';
import { useTranslation } from 'react-i18next';

import { RenderActionButtons } from '@/components/RenderActionButtons';
import { StatusSeenChipInTable } from '@/components/StatusSeenChipInTable';
import { TableRow } from '@/components/TableRow';
import { TableUpdatedBy } from '@/components/TableUpdatedBy';
import { CNMC_REVIEW_STATES } from '@/constants';
import { useUserOptions } from '@/features/users/hooks/useUserOptions';
import { PaginatedTableProps } from '@/types/table';
import { withAppTzDateFilter } from '@/utils/app-tz-date-range';
import { createTableFilterOptions } from '@/utils/table-filters';
import { formatRemainingTime } from '@/utils/time';

import { useDownloadCNMC } from '../../api/get-cnmc-download';
import { usePauseCNMC } from '../../api/post-cnmc-pause';
import { useProcessCNMC } from '../../api/post-cnmc-process';
import { CNMC, CNMCStateEnum, CNMCFileStateEnum } from '../../types';

export function CNMCsTable({
  items,
  isFetching,
  totalItems,
  activeItem,
  onSelect,
  onDelete,
  onUpdate,
  onFilesUpload,
  onPaginationChange,
  paginationValues,
}: PaginatedTableProps<CNMC> & {
  onPaginationChange: ({ limit, offset }: { limit: number; offset: number }) => void;
  paginationValues: { currentPage: number; itemsPerPage: number };
}) {
  const { t } = useTranslation();
  const userOptions = useUserOptions();

  const { mutate: processCNMC, isPending: isProcessingCNMC } = useProcessCNMC();
  const { mutate: pauseCNMC, isPending: isPausingCNMC } = usePauseCNMC();
  const { mutate: downloadCNMC } = useDownloadCNMC();

  const options: Partial<TablePaginatedOptions> = {
    searchBarPlaceholder: t('Buscar por nombre o descripción'),
    dateFilter: withAppTzDateFilter({
      name: 'created_at',
      placeholder: t('Fecha de creación'),
      onChange: () => {},
    }),
    filters: [
      {
        name: 'state',
        placeholder: t('Estado'),
        options: CNMC_REVIEW_STATES.map(reviewState => ({
          label: t(reviewState.label),
          icon: reviewState.icon,
          value: reviewState.value,
        })),
        onChange: () => {},
      },
      {
        name: 'updated_by',
        placeholder: t('Actualizado por'),
        options: createTableFilterOptions({
          options: userOptions,
          allOptionLabel: t('Actualizado por cualquiera'),
        }),
        onChange: () => {},
      },
    ],
    pagination: {
      changePage: page =>
        onPaginationChange({
          limit: paginationValues.itemsPerPage,
          offset: (page - 1) * paginationValues.itemsPerPage,
        }),
      changeRows: rows => onPaginationChange({ limit: rows, offset: 0 }),
      currentPage: paginationValues.currentPage,
      itemsPerPage: paginationValues.itemsPerPage,
    },
  };
  const columns: Column<CNMC>[] = [
    {
      id: 'name',
      title: t('Nombre'),
      sortable: true,
      onColumnClick: item => onSelect?.(item),
      render: item => <TableRow title={item.name} content={item.description} />,
    },
    {
      id: 'remaining_time',
      title: t('Tiempo restante'),
      sortable: true,
      tooltip: t('Tiempo que falta para finalizar el procesado de los vídeos'),
      onColumnClick: item => onSelect?.(item),
      render: item => <TableRow content={formatRemainingTime(item.estimated_time)} />,
    },
    // {
    //   id: 'emails',
    //   title: t('Emails'),
    //   sortable: true,
    //   onColumnClick: item => onSelect?.(item),
    //   render: item => (
    //     <IconCounter
    //       icon={MailIcon}
    //       count={item.recipients.trim() ? item.recipients.split(',').length : 0}
    //     />
    //   ),
    // },
    {
      id: 'state',
      title: t('Estado'),
      sortable: true,
      onColumnClick: item => onSelect?.(item),
      render: item => (
        <StatusSeenChipInTable<CNMC, CNMCStateEnum>
          classname="w-fit"
          review_state={item.state}
          reviewStates={CNMC_REVIEW_STATES}
          item={undefined}
        />
      ),
    },
    {
      id: 'updated_by',
      title: t('Actualizado por'),
      render: item => {
        return <TableUpdatedBy item={item} />;
      },
    },
    {
      id: 'actions',
      title: '',
      className: 'items-end',
      width: '5%',
      render: item => {
        const isProcessingRow = item.state === CNMCStateEnum.PROCESSING;
        const hasMkvFiles =
          Array.isArray(item.files) && item.files.some(file => file.uri.endsWith('.mkv'));
        const hasUploadingMkvForCNMC =
          Array.isArray(item.files) &&
          item.files.some(
            f =>
              (f.uri?.toLowerCase().endsWith('.mkv') ?? false) &&
              f.state === CNMCFileStateEnum.UPLOADING
          );
        const canDownloadReport = item.state === CNMCStateEnum.PROCESSED && item.report_id; // Assuming item.report_id exists for the report

        return (
          <RenderActionButtons
            item={item}
            onUpdate={onUpdate}
            onPlay={!isProcessingRow ? () => processCNMC({ cnmcId: item.id }) : undefined}
            onPause={isProcessingRow ? () => pauseCNMC({ cnmcId: item.id }) : undefined}
            onReportDownload={() => downloadCNMC({ cnmcId: item.id })}
            onFilesUpload={onFilesUpload}
            onDelete={onDelete}
            disabledFields={{
              play: !hasMkvFiles || hasUploadingMkvForCNMC || isProcessingCNMC || isPausingCNMC,
              pause: isProcessingCNMC || isPausingCNMC,
              reportDownload: !canDownloadReport,
            }}
            tooltips={{
              play: t('Procesar IA'),
              pause: t('Parar proceso IA'),
              delete: t('Eliminar proceso'),
            }}
          />
        );
      },
    },
  ];

  return (
    <TablePaginated
      options={options}
      columns={columns}
      data={items}
      activeItem={activeItem}
      totalItems={totalItems}
      isFetching={isFetching}
      ignoreEmpty
    />
  );
}
